import { css } from '@emotion/react'
import { useEditorState } from '@tiptap/react'
import { Spacing, Color, ZIndex } from '@opallabs/gem'
import { DropdownMenu, Icon, Button } from '@opallabs/lapidary'
import { localizeCommandsForOS } from '@opallabs/satellite'
import { useTextEditor } from '../../context'
import { MemoizedPopover } from './MemoizedPopover'

export const tableOptionsMenuClassName = 'rich-text-table-options'

const menuItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  columnGap: 12,
}
const destructiveStyle = { color: Color.light.Text.Warning }
const shortcutStyle = { color: Color.light.Text.Gray }
const separatorStyle = { backgroundColor: Color.light.Surface.N200 }
const buttonStyle = css({
  blockSize: 24,
  inlineSize: 24,
  display: 'inline-block',
  '&.icon-only': { paddingInline: Spacing.XS },
  '&[aria-expanded=true]': { borderColor: Color.light.Surface.A200 },
})

export const TableControlButton: React.FC = () => {
  const { editor } = useTextEditor()
  const isActive = useEditorState({
    editor,
    selector: (snapshot) => ({
      tableHeader: snapshot.editor.isActive('tableHeader'),
    }),
  })

  return (
    <DropdownMenu.Root>
      <MemoizedPopover label="Edit Table">
        <DropdownMenu.Trigger asChild>
          <Button
            css={buttonStyle}
            variant="ghost"
            name="Edit Table"
            icon={<Icon glyph="gem-more-horizontal" ariaHidden />}
          />
        </DropdownMenu.Trigger>
      </MemoizedPopover>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={tableOptionsMenuClassName}
          align="start"
          side="bottom"
          onCloseAutoFocus={() => editor.commands.focus()}
          zIndex={ZIndex.Shroud.Popover}
        >
          <DropdownMenu.Item
            onClick={() => editor.commands.addColumnBefore()}
            style={menuItemStyle}
          >
            Add Column Left
            <span css={shortcutStyle}>
              {localizeCommandsForOS('meta + shift + left arrow')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onClick={() => editor.commands.addColumnAfter()}
            style={menuItemStyle}
          >
            Add Column Right
            <span css={shortcutStyle}>
              {localizeCommandsForOS('meta + shift + right arrow')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            disabled={isActive.tableHeader}
            onClick={() => {
              // Only allows adding a row above if current row is not table header
              if (!isActive.tableHeader) {
                editor.commands.addRowBefore()
              }
            }}
            style={menuItemStyle}
          >
            Add Row Above
            <span css={shortcutStyle}>
              {localizeCommandsForOS('meta + shift + up arrow')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onClick={() => editor.commands.addRowAfter()}
            style={menuItemStyle}
          >
            Add Row Below
            <span css={shortcutStyle}>
              {localizeCommandsForOS('meta + shift + down arrow')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator style={separatorStyle} />
          <DropdownMenu.Item
            onClick={() => editor.commands.deleteColumn()}
            style={{ ...menuItemStyle, ...destructiveStyle }}
          >
            Delete Column
            <span css={shortcutStyle}>
              {localizeCommandsForOS('ctrl + alt + backspace')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onClick={() => editor.commands.deleteRow()}
            style={{ ...menuItemStyle, ...destructiveStyle }}
          >
            Delete Row
            <span css={shortcutStyle}>
              {localizeCommandsForOS('meta + alt + backspace')}
            </span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator style={separatorStyle} />
          <DropdownMenu.Item
            onClick={() => editor.commands.deleteTable()}
            style={{ ...menuItemStyle, ...destructiveStyle }}
          >
            Delete Table
            <span css={shortcutStyle}>
              {localizeCommandsForOS('shift + alt + backspace')}
            </span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
